<script setup>
import { ref, computed, watch } from "vue";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import DeleteConfirmModal from "@/components/Generics/DeleteConfirmModal.vue";

const props = defineProps({
  subscriptionIds: {
    type: Array,
    required: true,
  },
});

const emit  = defineEmits(["close", "refresh"]);
const store = useSubscriptionStore();

const isDeleting = ref(false);

// ✅ Contagem atual (store) — pode mudar após deleção/limpeza
const selectedCount = computed(() => store.selectedSubscriptionIds.length);

// ✅ Congela contagem e IDs (alvo real do delete)
const frozenCount = ref(0);
const frozenIds   = ref([]);

function freezeSnapshot() {
  const fromProps = Array.isArray(props.subscriptionIds) ? props.subscriptionIds : [];
  const fromStore = Array.isArray(store.selectedSubscriptionIds) ? store.selectedSubscriptionIds : [];

  // Prioriza props.subscriptionIds (valor recebido do componente pai)
  const ids = fromProps.length ? fromProps : fromStore;

  frozenIds.value   = [...ids];
  frozenCount.value = frozenIds.value.length;
}

// ✅ Mantém snapshot atualizado enquanto não está deletando
watch(
  () => [props.subscriptionIds, store.selectedSubscriptionIds],
  () => { if (!isDeleting.value) freezeSnapshot(); },
  { immediate: true, deep: true }
);

const modalTitle = "Apagar Assinaturas em Lote";

const messageTitle = computed(() => {
  const n = frozenCount.value || selectedCount.value;
  if (n <= 1) return "Você está prestes a apagar 1 assinatura selecionada.";
  return `Você está prestes a apagar ${n} assinaturas selecionadas.`;
});

const messageSubtitle = computed(() => "Esta ação não pode ser desfeita.");

function buildErrorMessage(error) {
  return (
    error?.response?.data?.message
    ?? error?.message
    ?? "Ocorreu um erro ao excluir as assinaturas."
  );
}

async function onConfirmDelete() {
  freezeSnapshot();
  isDeleting.value = true;

  try {
    if (!frozenIds.value.length)
      throw new Error("Nenhuma assinatura selecionada para exclusão.");

    // Garante que o store delete exatamente o snapshot
    store.selectedSubscriptionIds = [...frozenIds.value];

    await store.deleteSelectedSubscriptions();
  } catch (error) {
    isDeleting.value = false;
    throw new Error(buildErrorMessage(error)); // toast vem do DeleteConfirmModal
  }
}

function handleSuccess() {
  emit("refresh");
  emit("close");
}
</script>

<template>
  <DeleteConfirmModal
    :show="true"
    :title="modalTitle"
    :messageTitle="messageTitle"
    :messageSubtitle="messageSubtitle"
    confirmText="Sim, apagar"
    cancelText="Cancelar"
    loadingText="Apagando..."
    :onConfirm="onConfirmDelete"
    @success="handleSuccess"
    @close="!isDeleting && emit('close')"
  />
</template>